/* ================================================================
   cart-progress — external JS (v9.2+, JL custom)
   Loaded from layout/theme.liquid so Shopify's editor section-refresh
   never leaves the progress bar unbound. Reads data-* attributes off
   each [data-cart-progress] root; state classes on the root:
     is-empty | is-progress | is-complete | just-started
   and per-step: is-active | is-next.

   v16 changes (Aug 27 2026) — heading/subheading render as HTML:
     Merchant can now put simple markup in any heading or subheading
     setting — most usefully <br> to force a line break at the exact
     word they choose. Combined with the CSS text-wrap:pretty on
     .cart-progress__heading and .cart-progress__subheading (see
     cart-progress.css v16), single-word orphans on the last line
     get avoided both automatically and manually.

   v15 changes (Aug 27 2026) — actually apply the stage overrides:
     v14 read the stage attributes via element.dataset.headingStage1
     (etc.), which returns undefined because the DOM does not
     camelCase a hyphen-then-digit sequence. Fixed by reading with
     getAttribute directly, which bypasses the naming quirk entirely.

   v14 changes (Aug 27 2026) — stage-specific heading/subheading:
     Merchant can now set heading and subheading text per progress
     stage (Stage 1 = 1 item lit, Stage 2 = 2 items lit, and so on).
     Reads data-heading-stage-N / data-subheading-stage-N off the
     root; falls back to the base cart_progress_heading /
     cart_progress_subheading when a stage is left blank. The final
     stage still uses cart_progress_heading_complete /
     cart_progress_subheading_complete via the is-complete branch.

   v13 changes (Aug 27 2026) — kill the "empty [data-products]" false 0:
     On the very first add (fresh drawer opening on a previously-empty
     cart), Impulse creates the line item DOM asynchronously — the
     [data-products] wrapper is briefly empty even after cart:build
     fires. v12's readFromDom returned wrap.children.length in that
     window, applying count=0 and wiping the bar back to the empty
     state. Reduce-from-populated-drawer worked because the wrap was
     always populated. Two changes fix it:

       1) readFromDom now returns null (not 0) when no quantity inputs
          are found. Refusing to guess lets the fetch fallback fire
          instead of clobbering with a stale 0.
       2) A MutationObserver on #CartDrawer [data-products] catches
          the moment Impulse finishes injecting the line items —
          debounced 50ms, then re-reads and applies. Works regardless
          of when cart:build fires relative to the DOM update.

   v12 changes (Aug 27 2026) — match the reference Impulse behaviour:
     Studied the older "June - Layout Scroll Test" theme where the
     cart-progress worked as expected. Two things were different from
     v11 and both were wrong in v11:

     1) Bar position is the MIDPOINT between the last active dot and
        the next inactive dot. So on a 3-dot meter with goal=3:
          count 1 → bar 25%  (halfway between dot 1 at 0% and dot 2 at 50%)
          count 2 → bar 75%  (halfway between dot 2 at 50% and dot 3 at 100%)
          count 3 → bar 100% (green, is-complete)
        The v11 "just before next anchor" formula (42% / 92%) felt
        cramped — the midpoint reads as a steady, honest halfway climb.

     2) Removed the is-goal-met middle state. is-complete now fires at
        meter-full only, and that's the ONLY celebration. No pink-copy
        "Best deal unlocked" phase where the meter is still climbing.
        The reference theme kept the copy pink "Almost there / Add X
        more" until every dot is lit, then flipped to green "Locked
        in". Cleaner and less confusing.

     Also fixed the "bar frozen on revert" bug: v11 read
     data-initial-count on every cart event, but Impulse only rewrites
     that attribute if it re-renders the whole drawer form. If it
     re-renders just [data-products], the attribute is stale and my
     apply put the meter back at the old count. Fix: use
     data-initial-count ONLY on the first bindRoot (initial page load
     truth), never on subsequent events.

     Priority chain on cart events, in order:
       1. event.detail.cart.item_count
       2. window.theme.cart / Shopify.cart globals
       3. Sum of input[name="updates[]"] inside [data-products]
       4. /cart.js fetch (with no-store + cache-buster) as last resort

     v9 pieces kept: URL cache-buster + no-store on the /cart.js
     fallback so if we do fall through, no CDN edge serves stale JSON.

     Also listens for change events on the drawer's qty inputs so the
     [-] / [+] buttons — which don't always dispatch cart:build in
     time — still update the meter promptly.
================================================================ */
(function () {
  function interpolate(t, count) {
    return String(t).replace(/\{\{\s*count\s*\}\}/g, count);
  }

  function fireConfetti(root) {
    var wrap = root.querySelector('.cart-progress__confetti');
    if (!wrap) return;
    wrap.innerHTML = '';
    var cs = getComputedStyle(root);
    var colors = [
      cs.getPropertyValue('--cp-fill').trim() || '#ff4f8b',
      cs.getPropertyValue('--cp-glow').trim() || '#ff87b0',
      cs.getPropertyValue('--cp-success').trim() || '#00c853',
      '#ffd166'
    ];
    var N = 18;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < N; i++) {
      var s = document.createElement('span');
      s.style.left = (Math.random() * 100) + '%';
      s.style.background = colors[i % colors.length];
      s.style.animationDelay = (Math.random() * 0.25) + 's';
      s.style.animationDuration = (1.1 + Math.random() * 0.9) + 's';
      s.style.transform = 'rotate(' + (Math.random() * 360) + 'deg)';
      frag.appendChild(s);
    }
    wrap.appendChild(frag);
    setTimeout(function () { wrap.innerHTML = ''; }, 2200);
  }

  function bindRoot(root) {
    if (root.__cpBound) return;
    root.__cpBound = true;

    var goal = parseInt(root.dataset.goal, 10) || 3;
    if (goal < 1) goal = 1;
    var stepCount = parseInt(root.dataset.steps, 10) || goal;
    if (stepCount < 1) stepCount = 1;

    var headingEl = root.querySelector('[data-progress-heading]');
    var subheadingEl = root.querySelector('[data-progress-subheading]');
    var fillEl = root.querySelector('[data-progress-fill]');
    var stepEls = root.querySelectorAll('[data-step]');
    var tplHeading = root.dataset.heading || '';
    var tplSubheading = root.dataset.subheading || '';
    var tplHeadingDone = root.dataset.headingComplete || '';
    var tplSubheadingDone = root.dataset.subheadingComplete || '';
    var enableConfetti = root.dataset.enableConfetti === 'true';
    var enableAnimations = root.dataset.enableAnimations === 'true';

    // v15: stage-specific text. Merchant sets heading/subheading per
    // stage (Stage 1 = 1 dot lit, Stage 2 = 2 dots lit, etc.) via
    // data-heading-stage-N / data-subheading-stage-N. Empty values
    // fall back to the base heading/subheading. The last stage is
    // covered by data-heading-complete / data-subheading-complete.
    //
    // NOTE: we use getAttribute directly instead of element.dataset,
    // because the DOM does NOT camelCase a hyphen followed by a digit —
    // `data-heading-stage-1` becomes dataset['headingStage-1'], not
    // dataset.headingStage1. v14 read via the wrong property and
    // always got undefined, which is why the merchant's stage 1 & 2
    // settings appeared to be ignored.
    var stageHeadings = [];
    var stageSubheadings = [];
    for (var st = 1; st <= stepCount; st++) {
      var h = root.getAttribute('data-heading-stage-' + st);
      var s2 = root.getAttribute('data-subheading-stage-' + st);
      stageHeadings.push(h && h.length ? h : null);
      stageSubheadings.push(s2 && s2.length ? s2 : null);
    }

    // Even distribution — each dot's threshold is a scaled portion of
    // the goal. When goal === stepCount this reduces to [1, 2, ..., n]
    // (the reference theme case). When goal < stepCount the last few
    // dots share the same threshold (meter still fills exactly at
    // goal). When goal > stepCount each dot represents multiple items.
    var thresholds = [];
    for (var s = 0; s < stepCount; s++) {
      thresholds.push(Math.ceil((s + 1) / stepCount * goal));
    }
    var meterFullCount = thresholds[thresholds.length - 1] || goal;

    root.__cpApply = function (count) {
      var remainingToGoal = Math.max(0, goal - count);
      var isEmpty = count === 0;
      var isComplete = count >= meterFullCount;

      // Find last active + next inactive dots for the midpoint fill.
      var lastActiveIdx = -1;
      var nextInactiveIdx = -1;
      for (var i = 0; i < stepCount; i++) {
        if (count >= thresholds[i]) {
          lastActiveIdx = i;
        } else if (nextInactiveIdx === -1) {
          nextInactiveIdx = i;
        }
      }

      // Bar pct = halfway between the last lit dot and the next unlit
      // dot. Special cases: empty cart at 0, fully full at 100, and
      // "count > 0 but no dot lit yet" (goal > stepCount edge case)
      // rides a linear ramp toward the first dot.
      var pct;
      if (isEmpty) {
        pct = 0;
      } else if (isComplete) {
        pct = 100;
      } else if (lastActiveIdx === -1) {
        // First dot not lit yet — linear approach so the bar still
        // creeps toward it.
        var firstT = thresholds[0] || 1;
        var firstPos = stepCount > 1 ? 0 : 100;
        pct = Math.min(firstPos, (count / firstT) * firstPos);
      } else {
        var lastPos = stepCount > 1 ? (lastActiveIdx / (stepCount - 1)) * 100 : 100;
        var nextPos = nextInactiveIdx === -1
          ? 100
          : (stepCount > 1 ? (nextInactiveIdx / (stepCount - 1)) * 100 : 100);
        pct = (lastPos + nextPos) / 2;
      }

      root.classList.remove('is-empty', 'is-progress', 'is-complete', 'just-started');
      if (fillEl) fillEl.style.setProperty('width', pct + '%', 'important');

      stepEls.forEach(function (el, i) {
        var active = count >= thresholds[i];
        el.classList.toggle('is-active', active);
        el.classList.remove('is-next');
      });

      if (nextInactiveIdx !== -1 && stepEls[nextInactiveIdx]) {
        stepEls[nextInactiveIdx].classList.add('is-next');
      }

      // Number of dots currently lit — drives per-stage heading/sub.
      var activeCount = lastActiveIdx + 1;

      // v16: heading/sub are set via innerHTML so <br>, <em>, etc.
      // from the merchant's setting text render as markup. The base
      // heading/subheading copy is escape-safe (Liquid's | escape on
      // the data attribute means the browser hands us the raw source
      // string), and the merchant is the only one supplying it, so
      // rendering as HTML is safe.

      // State class + copy
      if (isEmpty) {
        root.classList.add('is-empty');
        if (headingEl) headingEl.innerHTML = tplHeading;
        if (subheadingEl) subheadingEl.innerHTML = interpolate(tplSubheading, goal);
      } else if (isComplete) {
        root.classList.add('is-complete');
        if (headingEl) headingEl.innerHTML = tplHeadingDone || tplHeading;
        if (subheadingEl) subheadingEl.innerHTML = interpolate(tplSubheadingDone || tplSubheading, 0);
      } else {
        root.classList.add('is-progress');
        // v14: stage-specific heading/subheading override when the
        // merchant set one. activeCount is 1-based dot count (0 →
        // handled by is-empty branch); index into the stage arrays
        // is activeCount-1.
        var stageIdx = Math.max(0, activeCount - 1);
        var heading = (stageHeadings[stageIdx] != null) ? stageHeadings[stageIdx] : tplHeading;
        var sub = (stageSubheadings[stageIdx] != null) ? stageSubheadings[stageIdx] : tplSubheading;
        if (headingEl) headingEl.innerHTML = heading;
        if (subheadingEl) subheadingEl.innerHTML = interpolate(sub, remainingToGoal);
      }

      // Confetti — fires once per empty→complete run.
      if (isComplete && !root.dataset.confettiFired) {
        root.dataset.confettiFired = 'true';
        if (enableConfetti) fireConfetti(root);
      }
      if (!isComplete && root.dataset.confettiFired) {
        delete root.dataset.confettiFired;
      }

      if (enableAnimations && !isEmpty && count === 1) {
        root.classList.add('just-started');
        setTimeout(function () { root.classList.remove('just-started'); }, 600);
      }
    };

    // Seed from server-rendered data-initial-count on FIRST bind only.
    // Subsequent cart events read from live sources (event/globals/DOM),
    // so if Impulse doesn't re-render this snippet a stale attribute
    // can't freeze the meter.
    var seed = parseInt(root.dataset.initialCount, 10);
    if (!isNaN(seed) && seed >= 0) {
      root.__cpApply(seed);
    }
  }

  function readFromEvent(evt) {
    if (!evt || !evt.detail) return null;
    if (evt.detail.cart && typeof evt.detail.cart.item_count === 'number') {
      return evt.detail.cart.item_count;
    }
    if (typeof evt.detail.item_count === 'number') {
      return evt.detail.item_count;
    }
    return null;
  }

  function readFromGlobals() {
    var candidates = [
      window.theme && window.theme.cart,
      window.Shopify && window.Shopify.cart,
      window.impulse && window.impulse.cart
    ];
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i] && typeof candidates[i].item_count === 'number') {
        return candidates[i].item_count;
      }
    }
    return null;
  }

  function readFromDom() {
    var drawer = document.getElementById('CartDrawer')
      || document.querySelector('[data-cart-drawer]')
      || document.querySelector('#CartPageForm');
    if (!drawer) return null;
    var wrap = drawer.querySelector('[data-products]');
    if (!wrap) return null;

    var inputs = wrap.querySelectorAll('input[name="updates[]"]');
    if (inputs.length) {
      var total = 0;
      for (var i = 0; i < inputs.length; i++) {
        var v = parseInt(inputs[i].value, 10);
        if (!isNaN(v) && v > 0) total += v;
      }
      return total;
    }

    var qtyEls = wrap.querySelectorAll('[data-quantity]');
    if (qtyEls.length) {
      var total2 = 0;
      for (var j = 0; j < qtyEls.length; j++) {
        var raw = qtyEls[j].value || qtyEls[j].getAttribute('data-quantity');
        var val = parseInt(raw, 10);
        if (!isNaN(val) && val > 0) total2 += val;
      }
      return total2;
    }

    // v13: no qty inputs and no [data-quantity] means either the cart
    // is genuinely empty OR Impulse hasn't finished injecting the line
    // items yet. Return null — refuse to guess. Caller falls back to
    // /cart.js which knows the truth. Returning 0 here (as v12 did)
    // caused the "first add resets bar to 0%" bug: on a fresh drawer
    // open, [data-products] is briefly empty even after cart:build.
    return null;
  }

  function applyCartCount(n) {
    document.querySelectorAll('[data-cart-progress]').forEach(function (root) {
      if (root.__cpApply) root.__cpApply(n);
    });
  }

  function refresh() {
    fetch('/cart.js?_cp=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var n = (cart && cart.item_count) || 0;
        applyCartCount(n);
      })
      .catch(function () {});
  }

  function init() {
    var roots = document.querySelectorAll('[data-cart-progress]');
    if (!roots.length) return;
    roots.forEach(bindRoot);
  }

  function readCurrentCount(evt) {
    var n = readFromEvent(evt);
    if (n === null) n = readFromGlobals();
    if (n === null) n = readFromDom();
    return n;
  }

  function handleCartEvent(evt) {
    init();
    var n = readCurrentCount(evt);
    if (n !== null) {
      applyCartCount(n);
    } else {
      refresh();
    }
    // Safety-net follow-ups: catch a late re-render.
    setTimeout(function () {
      var later = readCurrentCount();
      if (later !== null) applyCartCount(later);
      else refresh();
    }, 300);
    setTimeout(function () {
      var later = readCurrentCount();
      if (later !== null) applyCartCount(later);
    }, 1200);
  }

  window.__cpInit = init;
  window.__cpRefresh = refresh;
  window.__cpApplyCartCount = applyCartCount;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  ['cart:refresh', 'cart:updated', 'cart:build', 'ajaxCart.afterCartLoad'].forEach(function (evt) {
    document.addEventListener(evt, handleCartEvent);
  });

  document.addEventListener('shopify:section:load', handleCartEvent);
  document.addEventListener('shopify:section:select', handleCartEvent);

  // Catch direct qty control changes too — some Impulse builds don't
  // dispatch cart:build until after the ajax completes, so the change
  // event fires first.
  document.addEventListener('change', function (evt) {
    var t = evt.target;
    if (!t || !t.matches) return;
    if (t.matches('input[name="updates[]"]') || t.matches('[data-quantity]')) {
      handleCartEvent(evt);
    }
  }, true);

  // Also observe [-] / [+] button clicks; Impulse debounces the
  // subsequent /cart/change.js request, so a delayed read catches the
  // committed state.
  document.addEventListener('click', function (evt) {
    var t = evt.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-decrement], [data-increment], .ajaxcart__qty-adjust, [data-action="minus"], [data-action="plus"]')) {
      setTimeout(handleCartEvent, 200);
      setTimeout(handleCartEvent, 800);
      setTimeout(handleCartEvent, 1600);
    }
  }, true);

  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (
            (n.matches && n.matches('[data-cart-progress]')) ||
            (n.querySelector && n.querySelector('[data-cart-progress]'))
          )) {
            handleCartEvent();
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // v13: watch [data-products] specifically. Impulse injects line
    // items into it asynchronously — cart:build sometimes fires before
    // that injection completes, especially on the first add after an
    // empty cart. Debounced re-read catches the moment items land.
    var productsObserver = null;
    function attachProductsObserver() {
      var drawer = document.getElementById('CartDrawer');
      if (!drawer) {
        setTimeout(attachProductsObserver, 200);
        return;
      }
      var wrap = drawer.querySelector('[data-products]');
      if (!wrap) {
        setTimeout(attachProductsObserver, 200);
        return;
      }
      if (productsObserver) productsObserver.disconnect();
      var debounceTimer;
      productsObserver = new MutationObserver(function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          var n = readCurrentCount();
          if (n !== null) applyCartCount(n);
          else refresh();
        }, 50);
      });
      productsObserver.observe(wrap, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'data-quantity']
      });
    }
    attachProductsObserver();
    // Re-attach if the drawer gets fully re-rendered (which drops the
    // old [data-products] node).
    document.addEventListener('cart:build', function () {
      setTimeout(attachProductsObserver, 100);
    });
  }
})();
